import { ReactElement, useState } from 'react'
import { Add } from '@mui/icons-material'
import {
  Autocomplete,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { ShearConnectionDTO } from '@api/fastAPI.schemas'

interface Props {
  options: ShearConnectionDTO[]
  onAdd: (connection: ShearConnectionDTO) => void
  'data-cy'?: string
}

const AddShearConnection = ({ options, onAdd, 'data-cy': dataCy }: Props): ReactElement => {
  // The select is a trigger, not a value: it reports the pick and resets itself
  // right away, so the row is always ready for the next addition. The selected
  // value is therefore always `null`; only the typed text is state, because MUI
  // fills the input with the picked option's label before calling `onChange`
  // and only resyncs it when the (here unchanged) value changes -- left alone,
  // the label would stick and hide every remaining option (MDG-5159).
  const [inputValue, setInputValue] = useState('')

  return (
    <TableRow>
      <TableCell>
        <IconButton size="small" disableRipple sx={{ cursor: 'auto', pl: 0 }}>
          <Add fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell sx={{ width: 300 }}>
        <Stack direction="row" spacing={2}>
          <Autocomplete
            disablePortal
            size="small"
            options={options}
            sx={{ width: 300 }}
            renderInput={params => (
              <TextField {...params} label="auswählen zum hinzufügen" data-cy={dataCy} />
            )}
            getOptionLabel={option => `${option.name}`}
            renderOption={(props, option) => (
              <Typography {...props} data-cy={`${dataCy}-option`}>
                {option.name}
              </Typography>
            )}
            value={null}
            inputValue={inputValue}
            onInputChange={(_, next) => setInputValue(next)}
            onChange={(_, next) => {
              setInputValue('')
              if (next) onAdd(next)
            }}
          />
        </Stack>
      </TableCell>
    </TableRow>
  )
}

export default AddShearConnection
